import React, {Fragment, useState, useEffect, useRef} from 'react';

import {get, assign, trim, remove} from 'lodash-es';
import PropTypes from 'prop-types';

import {GLOBAL_NAV_SEARCH_INPUT_FIELD_ID} from 'static/three-oh/src/components/GlobalLayout/GlobalNav/GlobalNavSearch/constants';
import {MAX_NUMBER_OF_RECENT_SEARCHES} from 'static/three-oh/src/constants/search';
import {eventStream, getEventNameForEventStream} from 'static/three-oh/utils/sessionActivity';

import {INACTIVE_INDEX_VALUE} from '../../constants';
import GlobalNavContentDrawer from '../GlobalNavContentDrawer/GlobalNavContentDrawer';
import SearchForm from './SearchForm/SearchForm';
import SearchSuggestions from './SearchSuggestions/SearchSuggestions';
import styles from './styles';

const propTypes = {
  isDesktopDisplay: PropTypes.bool.isRequired,
  handleOnCancel: PropTypes.func.isRequired,
  handleOnClickOff: PropTypes.func.isRequired,
  handleOnEscape: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  setOpenIndex: PropTypes.func.isRequired,
  show: PropTypes.bool,
  userProperties: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};

const defaultProps = {
  isDesktopDisplay: false,
};

const GlobalNavSearch = React.forwardRef((props, ref) => {
  const {searchButtonRef, browseButtonRef} = ref;
  const [queryTerm, setQueryTerm] = useState('');
  const [suggestionsAreActive, setActiveStateOfSuggestions] = useState(false);
  const [suggestionsAreVisible, setVisibilityOfSuggestions] = useState(false);
  const [throttledQueryTerm, setThrottledQueryTerm] = useState('');
  const recentSearchTerms = 'recentSearchTerms';

  useEffect(() => {
    const {isAuthenticated} = props.userProperties;
    const initializeRecentSearchStorage = !sessionStorage.getItem(recentSearchTerms);
    if (isAuthenticated && initializeRecentSearchStorage) {
      // Values stored in the 'sessionStorage' property of the browser's
      // 'window' object have to be stored as a string.
      sessionStorage.setItem(recentSearchTerms, '');
    }
  }, []); // Do this once -- when the component mounts.

  // Update the list of recent searches stored in sessionStorage.
  const updateRecentSearchList = () => {
    const recentSearchTermsString = sessionStorage.getItem(recentSearchTerms);
    const recentSearchTermsArray = recentSearchTermsString.split(',');
    const queryTermAlreadyListed = recentSearchTermsArray.includes(queryTerm);
    const queryIsPresent = Boolean(trim(queryTerm));

    if (queryIsPresent && !queryTermAlreadyListed) {
      recentSearchTermsArray.push(queryTerm);
    }

    // Remove falsey inputs created by blank strings.
    remove(recentSearchTermsArray, (term) => !term);

    if (recentSearchTermsArray.length > MAX_NUMBER_OF_RECENT_SEARCHES) {
      recentSearchTermsArray.shift();
    }
    sessionStorage.setItem('recentSearchTerms', recentSearchTermsArray);
  };

  const trackUserEvent = (eventName, properties = {}, click = true) => {
    const eventType = click ? 'click' : 'keydown';
    const componentProperties = {
      user_invoked_search: true
    };
    const eventProperties = assign(componentProperties, properties);

    const newEventProps = {
      event_name: getEventNameForEventStream('Content', 'Initiate', 'Search'),
      user_id: props.user.id,
      dimension_properties: {
        description: `Global nav search query term: ${eventName}`,
        legacy_event_name: `query_term_${eventName}`,
      }
    };
    eventStream(newEventProps, {userId: get(props, 'user.id'), name: `query_term_${eventName}`, type: eventType, properties: eventProperties});
  };

  const onQueryTermSubmission = (query) => {
    const {userProperties, history} = props;
    const isStandard = get(query, 'source_object_type') === 'Standard';
    const userIsAuthenticated = get(userProperties, 'isAuthenticated');
    const queryIsPresent = isStandard || Boolean(trim(query));

    // Put the query on sessionStorage.
    if (userIsAuthenticated && queryIsPresent) {
      updateRecentSearchList();
    }

    const properties = {
      user_id: props.user.id,
      active_term: query || ''
    };
    trackUserEvent('submit', properties, true);

    let url;
    if (isStandard) {
      url = `/search?needle=&standard_ids=${query.source_object_id}`;
    } else {
      url = `/search?needle=${query}`;
    }

    history.push(url);
  };

  // Handle dismissal of the suggestion list.
  const onSuggestionDismiss = () => {
    setActiveStateOfSuggestions(false);
    setVisibilityOfSuggestions(false);
    searchButtonRef.current.focus();
  };

  // Handle a search-suggestion's 'tab' event.
  const onSuggestionTab = () => {
    const inputFieldElement = document.getElementById(GLOBAL_NAV_SEARCH_INPUT_FIELD_ID);
    if (inputFieldElement) {
      inputFieldElement.focus();
    }
  };

  const searchContentDrawerRef = useRef(null);
  const searchForm = (
    <SearchForm
      queryTerm={queryTerm}
      setQueryTerm={setQueryTerm}
      user={props.user}
      userProperties={props.userProperties}
      history={props.history}
      setVisibilityOfSuggestions={setVisibilityOfSuggestions}
      setActiveStateOfSuggestions={setActiveStateOfSuggestions}
      setThrottledQueryTerm={setThrottledQueryTerm}
      isDesktopDisplay={props.isDesktopDisplay}
      onQueryTermSubmission={onQueryTermSubmission}
      ref={{
        searchButtonRef: searchButtonRef,
        browseButtonRef: browseButtonRef,
        searchContentDrawerRef: searchContentDrawerRef,
      }}
      handleOnEscape={props.handleOnEscape}
      handleOnCancel={props.handleOnCancel}
    />
  );
  const searchSuggestions = (
    <SearchSuggestions
      user={props.user}
      userProperties={props.userProperties}
      history={props.history}
      suggestionsAreActive={suggestionsAreActive}
      suggestionsAreVisible={suggestionsAreVisible}
      throttledQueryTerm={throttledQueryTerm}
      onSuggestionDismiss={onSuggestionDismiss}
      onSuggestionTab={onSuggestionTab}
      trackUserEvent={trackUserEvent}
      onQueryTermSubmission={onQueryTermSubmission}
    />
  );
  const desktopSearchDisplay = (
    <>
      {props.show ? searchForm : null}
      <GlobalNavContentDrawer
        show={props.show}
        additionalClassName={styles.contentDrawer}
        onCancel={() => props.handleOnCancel(searchButtonRef)}
        onClickOff={(event) => props.handleOnClickOff(event, searchButtonRef)}
        onEscape={() => {
          props.setOpenIndex(INACTIVE_INDEX_VALUE);
          props.handleOnEscape(searchContentDrawerRef, searchButtonRef);
        }}
        ref={searchContentDrawerRef}
        isSearchContentDrawer
      >
        {searchSuggestions}
      </GlobalNavContentDrawer>
    </>
  );
  const mobileSearchDisplay = (
    <GlobalNavContentDrawer
      show={props.show}
      onCancel={() => props.handleOnCancel(searchButtonRef)}
      onClickOff={(event) => props.handleOnClickOff(event, searchButtonRef)}
      onEscape={() => {
        props.setOpenIndex(INACTIVE_INDEX_VALUE);
        props.handleOnEscape(searchContentDrawerRef, searchButtonRef);
      }}
      ref={searchContentDrawerRef}
    >
      <div className={styles.mobileContainer}>
        {searchForm}
        {searchSuggestions}
      </div>
    </GlobalNavContentDrawer>
  );

  return props.isDesktopDisplay ? desktopSearchDisplay : mobileSearchDisplay;
});

GlobalNavSearch.propTypes = propTypes;
GlobalNavSearch.defaultProps = defaultProps;

export default GlobalNavSearch;
